import React from "react";
import "./Users.scss";
import { Card, Container, Button, NavBar, ConfirmationDialog, DataGrid, Icon, CardHeader } from "../../shared/components";
import * as coreApi from "../../shared/core-api-client";
import * as _ from "lodash";
import EditUserDialog from "./EditUser";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";
import { useSettings } from "../../shared/providers/SettingsProvider";
import RequiresTags from "../../shared/components/RequiresTags";

function Users(props) {
    const [refreshCounter, setRefreshCounter] = React.useState(0);
    const [data, setData] = React.useState<coreApi.UserListModel[]>([]);
    const [roles, setRoles] = React.useState<coreApi.RoleLookup[]>([]);
    const [groups, setGroups] = React.useState<coreApi.GroupLookup[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [showEdit, setShowEdit] = React.useState(false);
    const [activeItem, setActiveItem] = React.useState<coreApi.UserListModel>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const settings = useSettings();

    React.useEffect(() => {
        setIsLoading(true);
        new coreApi.UsersClient(settings.coreApiUrl).search(undefined, undefined, undefined, settings.domainName)
            .then(result => setData(result))
            .then(() => new coreApi.RolesClient(settings.coreApiUrl).getLookups(settings.domainName))
            .then(result => setRoles(result))
            .then(() => new coreApi.GroupsClient(settings.coreApiUrl).getLookups(settings.domainName))
            .then(result => setGroups(result))
            .finally(() => setIsLoading(false));
    }, [refreshCounter]);

    const onUserCreatedOrUpdated = () => {
        setRefreshCounter(refreshCounter + 1);
    }

    const createOrEditUser = (model) => {
        setActiveItem(model);
        setShowEdit(true);
    }

    const deleteUser = (model) => {
        setActiveItem(model);
        setConfirmOpen(true);
    }

    const onDeleteConfirmed = () => {
        new coreApi.UsersClient(settings.coreApiUrl).delete(activeItem.id)
            .then(() => {
                props.showSuccess("User deleted successfully.");
                setRefreshCounter(refreshCounter + 1);
            })
            .catch(error => {
                props.showError("Unable to delete user.  Please try again.");
            });
    }

    return (
        <>
            <NavBar
                title="Manage Users">
            </NavBar>

            <Container className="plush users-list" maxWidth="lg">
                <Card className="col-fill">
                    <CardHeader title="User List" action={
                        <RequiresTags tags={[["users::create"]]}>
                            <Button className="align-self-center" size="small" variant="contained" color="success" onClick={() => createOrEditUser(null)}>
                                <Icon fontSize="small">add</Icon>
                                Add User
                            </Button>
                        </RequiresTags>
                    } />
                    <DataGrid
                        disableRowSelectionOnClick
                        loading={isLoading}
                        columns={[
                            { headerName: 'Name', field: 'name', flex: 1 },
                            { headerName: 'Email', field: 'emailAddress', flex: 1 },
                            { headerName: 'Roles', field: 'roles', flex: 1, renderCell: (e) => (e.row.roles.toSorted() ?? []).map(roleId => roles.find(role => role.id === roleId)?.name).filter(r => Boolean(r)).join(", ") },
                            { headerName: 'Groups', field: 'groups', flex: 1, renderCell: (e) => (e.row.groups.toSorted() ?? []).map(groupId => groups.find(group => group.id === groupId)?.name).filter(r => Boolean(r)).join(", ") },
                            {
                                field: "actions", width: 150, type: "actions", getActions: (r) => [
                                    <RequiresTags tags={[["users::update"]]}>
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { createOrEditUser(r.row); e.preventDefault(); }} title="Edit User"><Icon fontSize="small">edit</Icon></Button>
                                    </RequiresTags>,
                                    <RequiresTags tags={[["users::delete"]]}>
                                        <Button size="small" variant="contained" color="error" onClick={(e) => { deleteUser(r.row); e.preventDefault(); }} title="Delete User"><Icon fontSize="small">delete</Icon></Button>
                                    </RequiresTags>
                                ]
                            }
                        ]}
                        rows={data}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: 'name', sort: 'asc' }
                                ]
                            }
                        }}
                    />
                </Card>
            </Container>

            <EditUserDialog
                key={activeItem?.id}
                open={showEdit}
                setOpen={setShowEdit}
                roles={roles}
                groups={groups}
                user={activeItem}
                onSuccess={onUserCreatedOrUpdated}
            />

            <ConfirmationDialog
                title="Delete User?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDeleteConfirmed}
            >
                {`Are you sure you want to delete ${activeItem?.name}?`}
            </ConfirmationDialog>

        </>
    );
}

export default connect(
    null,
    { showSuccess, showError })(Users);